import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import {
  Box,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  Text,
  useWidgetViewState,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import { AdvancedFilters } from "#app/components/filters/advanced-filters";
import { ExpandableSearch } from "@salestrak/ui";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import type { FilterState } from "#app/lib/filters/types";
import { useFilters } from "#app/lib/hooks/use-filters";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { parseWidgetFilters, widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { IssueTable } from "../shared/issue-table";
import { BAD_AGE_QUERY, OFF_TRACK_QUERY } from "#app/widgets/dashboards/widget-queries";
import {
  useEntityList,
  useEntityListTotal,
  type EntityListTransientFilters,
} from "./use-entity-list";
import type { Omittable } from "@salestrak/ui";

/**
 * `entity-list` — the Add-card "Project list" table: the scoped projects listed
 * through the shared `IssueTable` (real library `DataTable` + `useIssueColumns`),
 * so every cell routes through `#app/components/entity` and the rows stay live (open,
 * change stage, edit assignees, age dialog). Reads `useEntityList` (scope + Filters
 * + search fold) for the ONE `where` its table pages over — the table windows
 * through EVERY project in scope via per-group server queries, never a capped
 * chunk. The header adds the transient Only-Me + advanced-filter controls that
 * narrow THIS card only.
 */
export const entityListConfigSchema = z
  .looseObject({
    sort: z.enum(["created", "updated", "name"]).default("created"),
    groupBy: z.enum(["none", "stage", "workflow", "age", "issueType"]).default("none"),
    home: z.boolean().default(false),
    showTitleCount: z.boolean().default(false),
    filters: widgetFiltersSchema.nullish(),
  });

export type EntityListConfig = z.infer<typeof entityListConfigSchema>;
type EntityListGroupBy = EntityListConfig["groupBy"];

/** The advanced builder opens showing one empty row (matches the settings editor). */
const SEEDED_EMPTY_QUERY: FilterState = {
  logic: "AND",
  blocks: [{ logic: "AND", filters: [{ field: "", operator: "eq", value: null }] }],
};

/** Transient view-state this card's header writes and its body reads (shape over
 *  the kit's opaque `WidgetViewState` bag). Never persisted: the persisted config
 *  query seeds the visible header filter, then header-published state owns the query
 *  predicate for this viewer/session. Config `showArchived`/`dateRange` still fold
 *  from persisted settings — see `EntityListTransientFilters`. */
type EntityListViewState = EntityListTransientFilters & Omittable<{
  filterCount?: number;
  groupBy?: EntityListGroupBy;
}>;

const GROUP_OPTIONS: Array<{ value: EntityListGroupBy; label: string }> = [
  { value: "none", label: "None" },
  { value: "stage", label: "Stage" },
  { value: "workflow", label: "Workflow" },
  { value: "age", label: "Age" },
  { value: "issueType", label: "Project type" },
];

function groupLabel(value: EntityListGroupBy) {
  return GROUP_OPTIONS.find((option) => option.value === value)?.label ?? "None";
}

/**
 * The card body's toolbar controls: a transient, per-viewer grouping choice,
 * Only-Me toggle, advanced-filter popover, and search that narrow THIS card only.
 * Grouping is seeded from persisted Settings → Data, then lives in view-state
 * for this viewer/session (never persisted — same contract as the filters).
 */
function EntityListToolbar({
  groupBy,
  onGroupByChange,
  onSearchChange,
  seedQuery,
  seedQueryKey,
}: Omittable<{
  groupBy: EntityListGroupBy;
  onGroupByChange: (value: EntityListGroupBy) => void;
  onSearchChange: (value: string) => void;
  seedQuery?: FilterState | null;
  seedQueryKey: string;
}>) {
  const activeGroup = groupBy !== "none";
  const groupChipLabel = activeGroup ? groupLabel(groupBy) : "Group";

  return (
    <Box paddingX="md" paddingY="xs">
      <Box direction="row" align="center" justify="between" gap="sm">
        <Menu>
          <MenuTrigger
            render={
              <FilterToggle
                size="xs"
                icon="layers"
                label={groupChipLabel}
                selected={activeGroup}
                onClear={activeGroup ? () => onGroupByChange("none") : undefined}
              />
            }
          />
          <MenuPopup align="start" className="min-w-[180px]">
            {GROUP_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => onGroupByChange(option.value)}
              >
                {option.value === groupBy ? <Icon name="check" size="sm" /> : null}
                {option.label}
              </MenuItem>
            ))}
          </MenuPopup>
        </Menu>
        <EntityListToolbarActions
          key={seedQueryKey}
          onSearchChange={onSearchChange}
          seedQuery={seedQuery}
        />
      </Box>
    </Box>
  );
}

/**
 * The card toolbar's own filter controls: a transient, per-viewer Only-Me toggle +
 * advanced-filter popover that narrow THIS card only. The persisted config query
 * seeds the visible advanced filter so the designed narrowing is inspectable; the
 * header never persists edits back, and after it publishes, it owns the query
 * predicate for this viewer/session. Config-level `showArchived`/`dateRange` still
 * fold from persisted settings in `useEntityList`. Both hooks push the APPLIED
 * result into the shared `WidgetViewState`; Only-Me flows through `useEntityList`,
 * and the advanced query folds centrally in `useScopedWhere`.
 */
function EntityListToolbarActions({
  onSearchChange,
  seedQuery,
}: Omittable<{
  onSearchChange: (value: string) => void;
  seedQuery?: FilterState | null;
}>) {
  const { patch } = useWidgetViewState();
  // Member-scoped surfaces (My Work) already narrow every card to the current user,
  // so the per-card Only-Me toggle would be a redundant no-op — hide it there.
  const scope = useDashboardScope();
  const isMemberScope = scope?.entity?.type === "member";

  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle();

  useEffect(() => {
    patch({ onlyMe: isCurrentUserFilterApplied });
  }, [isCurrentUserFilterApplied, patch]);

  const query = useFilters(issueFilterConfig, {
    mode: "advanced",
    advancedState: seedQuery ?? SEEDED_EMPTY_QUERY,
  });
  const activeCount = query.activeFilterCount;

  useEffect(() => {
    // Active filters publish their built `where`; zero active rows clear it (a
    // half-built row shouldn't narrow the list).
    patch({ filterWhere: activeCount > 0 ? query.where : null, filterCount: activeCount });
  }, [query.where, activeCount, patch]);

  const clearQuery = useCallback(() => {
    query.clearAll();
    query.addBlock();
  }, [query]);

  return (
    <Box direction="row" align="center" gap="2xs">
      {isMemberScope ? null : (
        <CurrentUserButton
          size="xs"
          compact
          selected={isCurrentUserButtonSelected}
          onClick={handleCurrentUserPress}
          onClearClick={handleCurrentUserClear}
        />
      )}
      <Popover>
        <PopoverTrigger
          render={
            <FilterToggle
              size="xs"
              icon="filter"
              count={activeCount}
              onClear={clearQuery}
            />
          }
        />
        {/* The builder is an edge-to-edge form; the stock popover viewport
          * insets its content, so the padding is zeroed at the viewport slot. */}
        <PopoverPopup
          align="end"
          padding="none"
          className="w-[360px]"
        >
          <Box direction="column" maxHeight={440}>
            <ScrollArea>
              <AdvancedFilters config={issueFilterConfig} filters={query} />
            </ScrollArea>
          </Box>
        </PopoverPopup>
      </Popover>
      <ExpandableSearch size="xs" placeholder="Search projects..." onValueChange={onSearchChange} />
    </Box>
  );
}

function EntityListRender({ w, config }: WidgetRenderContext<EntityListConfig>) {
  // Transient header filters (Only-Me + advanced query) live in the card's shared
  // view-state; Only-Me is read here, and the advanced query folds in `useScopedWhere`.
  const { state, patch } = useWidgetViewState();
  const transient = state as EntityListViewState;
  const groupBy = transient.groupBy ?? config.groupBy;
  const seedQuery = useMemo(
    () => parseWidgetFilters(config.filters).query as FilterState | null | undefined,
    [config.filters],
  );
  const seedQueryKey = useMemo(() => JSON.stringify(seedQuery ?? null), [seedQuery]);
  const [searchValue, setSearchValue] = useState("");
  // Search is a SERVER predicate (`name contains`, folded in the data layer) so
  // it narrows the whole scoped set, not just the loaded window — debounced so
  // typing doesn't fire a query per keystroke.
  const debouncedSearch = useDebouncedValue(searchValue, 300);
  const { where, orderBy } = useEntityList(config.sort, config.filters, debouncedSearch, {
    onlyMe: transient.onlyMe,
  });
  const tableQuery = useMemo(() => ({ where, orderBy }), [where, orderBy]);
  const handleGroupByChange = useCallback(
    (nextGroupBy: EntityListGroupBy) => {
      patch({ groupBy: nextGroupBy });
    },
    [patch],
  );

  return (
    <>
      {config.home ? null : (
        <EntityListToolbar
          groupBy={groupBy}
          onGroupByChange={handleGroupByChange}
          onSearchChange={setSearchValue}
          seedQuery={seedQuery}
          seedQueryKey={seedQueryKey}
        />
      )}
      <IssueTable
        query={tableQuery}
        w={w}
        emptyIcon="list"
        emptyTitle="No projects in range"
        groupBy={config.home ? "none" : groupBy}
        home={config.home}
      />
    </>
  );
}

function EntityListTitleCount({ config }: WidgetRenderContext<EntityListConfig>) {
  if (!config.showTitleCount) return null;
  return <EntityListTitleCountValue config={config} />;
}

function EntityListTitleCountValue({ config }: { config: EntityListConfig }) {
  const { total, loading } = useEntityListTotal(config.sort, config.filters);
  if (loading) return null;
  return (
    <Text size="sm" weight="medium" variant="muted">
      · {total.toLocaleString()}
    </Text>
  );
}

const entityListSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: [
          { value: "created", label: "Newest first" },
          { value: "updated", label: "Recently updated" },
          { value: "name", label: "Name (A–Z)" },
        ],
      },
      {
        key: "groupBy",
        kind: "select",
        label: "Group by",
        icon: "layers",
        options: [
          { value: "none", label: "None" },
          { value: "stage", label: "Stage" },
          { value: "workflow", label: "Workflow" },
          { value: "age", label: "Age" },
          { value: "issueType", label: "Project type" },
        ],
      },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const entityListDef: WidgetModule<EntityListConfig> = {
  type: "entity-list",
  title: "Project list",
  icon: "list",
  // A table has no meaningful chart view — its home card maximizes straight to
  // the data view (D13, replacing a host-side type special-case).
  detailPresentation: "data",
  // Full-bleed body: the table's row dividers + selection wash reach the card
  // edge; the DataTable's own `paddingX` keeps cell content inset.
  bleed: true,
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6, defaultH: 5 },
  configSchema: entityListConfigSchema,
  titleAdornment: EntityListTitleCount,
  settings: entityListSettings,
  catalog: [
    {
      key: "entity-list.projects",
      label: "Project list",
      description: "A live, sortable list of the projects in scope.",
      section: "Table",
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { sort: "created" },
      defaultW: 6,
      defaultH: 5,
    },
    {
      key: "entity-list.needs-attention",
      label: "Needs attention",
      description: "Projects in scope that are At Risk or Off Track.",
      section: "Table",
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { sort: "updated", filters: { query: BAD_AGE_QUERY } },
      defaultW: 6,
      defaultH: 5,
    },
    {
      key: "entity-list.stale",
      label: "Stale projects",
      description: "Projects in scope that have gone Off Track.",
      section: "Table",
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { sort: "updated", filters: { query: OFF_TRACK_QUERY } },
      defaultW: 6,
      defaultH: 5,
    },
  ],
  render: EntityListRender,
};
